import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, isRef as _isRef, vModelSelect as _vModelSelect, normalizeProps as _normalizeProps, createElementVNode as _createElementVNode, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createVNode as _createVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  ref: "dropDownWrapper",
  class: "tw group relative text-base"
}
const _hoisted_2 = ["id", "name", "disabled", "required", "aria-describedby"]
const _hoisted_3 = ["value"]
const _hoisted_4 = ["for"]
const _hoisted_5 = ["data-key", "onClick"]
const _hoisted_6 = ["src"]

import { ref, useTemplateRef, computed, nextTick, watch, toRef } from "vue";
import SvgIcon from "./SvgIcon/SvgIcon.vue";
import HintText from "./SharedForm/HintText.vue";
import ValidationText from "./SharedForm/ValidationText.vue";
import { useBreakpoints, onClickOutside, onKeyStroke } from "@vueuse/core";
import useInputValidation from "../composables/use-input-validation";
import type { HTMLInputElementEvent, InputEventDetails } from "../types/SharedInput.type";
import type { SpriteSymbol } from "../types/GeneratedSpriteType";


export default /*@__PURE__*/_defineComponent({
  __name: 'SharedDropdown',
  props: {
    disabled: { type: Boolean },
    displayName: {},
    hint: {},
    hintPermanent: { type: Boolean },
    icons: {},
    imageUrls: {},
    label: {},
    modelValue: {},
    name: {},
    options: { default: () => ({}) },
    placeholder: {},
    required: { type: Boolean },
    value: {},
    forcedErrorMessage: {},
    requiredErrorMessage: {},
    dataTestName: { default: "" },
    ariaDescribedby: {}
  },
  emits: ["update:modelValue", "change-error-msg"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const nativeSelectRef = useTemplateRef("nativeSelect");
const dropDownWrapperRef = useTemplateRef("dropDownWrapper");
const customOptionsListRef = useTemplateRef("customOptionsList");

const isEm64 = useBreakpoints({ em64: "64em" }).greaterOrEqual("em64");
const isCustomDropdown = computed(() => isEm64.value);
const customOptionsList_isOpen = ref(false);
const customOptionsList_focusedOptionKey = ref<string | null | undefined>(null);
const hasPlaceholder = !!props.placeholder;
const generatedOptions = hasPlaceholder ? { ...{ "": props.placeholder }, ...props.options } : props.options;
const generatedIcons = hasPlaceholder && props.icons ? [""].concat(props.icons) : undefined;
const generatedImageUrls = hasPlaceholder && props.imageUrls ? [""].concat(props.imageUrls) : undefined;

const { isValidationVisible, errorMessage, handleInput } = useInputValidation(
	nativeSelectRef,
	props.displayName || props.name || "Feld",
	undefined,
	toRef(props, "forcedErrorMessage"),
	props.requiredErrorMessage
);

watch([isValidationVisible, errorMessage], () => {
	emit("change-error-msg", event as HTMLInputElementEvent, { errorMessage: errorMessage.value });
});

const selectedOption =
	props.modelValue === undefined
		? ref(props.value || "")
		: computed({
				get() {
					return props.modelValue;
				},
				set(val) {
					emit("update:modelValue", val || "");
				},
		  });

const hasError = computed(() => isValidationVisible.value);
const isPopulated = computed(() => !!selectedOption.value);

// Methods
const handleMousedownOnSelect = (e: Event) => {
	if (!isCustomDropdown.value) return;
	e.preventDefault();
	customDropDown_toggle();
};

const customDropDown_focusSelected = (customOptionsList_focusedOptionKey?: string | null | undefined) => {
	(
		customOptionsListRef.value?.querySelector(`button[data-key=${customOptionsList_focusedOptionKey}]`) as HTMLElement
	)?.focus();
};

const customDropDown_close = () => {
	customOptionsList_isOpen.value = false;
};

const customDropDown_toggle = () => {
	customOptionsList_isOpen.value = !customOptionsList_isOpen.value;
	if (!isCustomDropdown.value) return;

	if (customOptionsList_isOpen.value && selectedOption.value)
		nextTick(() => {
			customOptionsList_focusedOptionKey.value = selectedOption.value;
			customDropDown_focusSelected(customOptionsList_focusedOptionKey.value);
		});
};

const focusNativeSelect = () => {
	nextTick(() => nativeSelectRef.value?.focus());
};

const customDropDown_select = (key: string | number) => {
	if (key === "none") {
		key = "";
	}
	selectedOption.value = String(key);
	customDropDown_close();
	focusNativeSelect();
};

const handleSpaceAndEnter = (e: KeyboardEvent) => {
	if (!isCustomDropdown.value) return;
	e.preventDefault();
	customDropDown_toggle();
};

const handleArrowUpAndDown = (e: KeyboardEvent) => {
	if (!isCustomDropdown.value) return;
	e.preventDefault();
	if (!customOptionsList_isOpen.value) return customDropDown_toggle();

	const optionsKeys = Object.keys(generatedOptions);
	const focusedIndex = optionsKeys.indexOf(customOptionsList_focusedOptionKey.value || "");
	const add = e.key === "ArrowUp" ? -1 : 1;
	const newIndex = Math.min(Math.max(0, focusedIndex + add), optionsKeys.length - 1);
	customOptionsList_focusedOptionKey.value = optionsKeys[newIndex];

	customDropDown_focusSelected(customOptionsList_focusedOptionKey.value);
};

const handleTap = (e: KeyboardEvent) => {
	if (!isCustomDropdown.value) return;
	if (customOptionsList_isOpen.value) {
		e.preventDefault();
		const key = document.activeElement?.getAttribute("data-key");
		if (key) customDropDown_select(key);
		customDropDown_close();
		focusNativeSelect();
	}
};

const handleEscape = () => {
	customDropDown_close();
	focusNativeSelect();
};

onKeyStroke([" ", "Spacebar", "Enter"], (e) => handleSpaceAndEnter(e), {
	target: nativeSelectRef,
});

onKeyStroke(["ArrowUp", "ArrowDown"], (e: KeyboardEvent) => handleArrowUpAndDown(e), {
	target: dropDownWrapperRef,
});

onKeyStroke("Tab", (e: KeyboardEvent) => handleTap(e), { target: dropDownWrapperRef });

onKeyStroke("Escape", () => handleEscape(), { target: dropDownWrapperRef });

onClickOutside(dropDownWrapperRef, () => customDropDown_close());

watch(
	() => selectedOption.value,
	() => {
		customDropDown_close();
		handleInput();
	}
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _withDirectives(_createElementVNode("select", _normalizeProps({
        id: _ctx.name,
        ref: "nativeSelect",
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(selectedOption) ? (selectedOption).value = $event : null)),
        name: _ctx.name,
        class: ["peer relative flex h-14 w-full select-none appearance-none items-center truncate rounded border-none bg-white pl-4 pr-10 outline outline-1 -outline-offset-1 focus-visible:outline-[3px] focus-visible:-outline-offset-[3px] focus-visible:outline-black-primary", {
					'text-gray-70': !customOptionsList_isOpen.value && !_ctx.disabled && !hasError.value && !isPopulated.value,
					'text-gray-80 outline-gray-70': !customOptionsList_isOpen.value && !_ctx.disabled && !hasError.value,
					'text-black-primary outline-[3px] outline-black-primary': customOptionsList_isOpen.value && !hasError.value,
					'outline-error focus-visible:outline-error': hasError.value,
					'focus-visible:text-black-primary supportsHover:hover:text-black-primary supportsHover:hover:outline-black-primary':
						!_ctx.disabled && !hasError.value,
					'cursor-pointer': !_ctx.disabled,
					'pointer-events-auto cursor-not-allowed text-gray-50': _ctx.disabled,
				}],
        style: {"-webkit-tap-highlight-color":"transparent"},
        disabled: _ctx.disabled,
        required: _ctx.required,
        [_ctx.dataTestName || ""]: '',
        "aria-describedby": _ctx.ariaDescribedby,
        onMousedown: handleMousedownOnSelect
      }), [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(generatedOptions), (optionValue, key) => {
          return (_openBlock(), _createElementBlock("option", {
            key: optionValue,
            value: key
          }, _toDisplayString(optionValue), 9, _hoisted_3))
        }), 128))
      ], 16, _hoisted_2), [
        [_vModelSelect, _unref(selectedOption)]
      ]),
      _createElementVNode("label", {
        for: _ctx.name,
        class: _normalizeClass(["pointer-events-none absolute -top-2.5 left-3 whitespace-nowrap bg-white px-1 text-sm", {
					'text-gray-80': !customOptionsList_isOpen.value && !_ctx.disabled && !hasError.value,
					'text-black-primary': customOptionsList_isOpen.value && !_ctx.disabled && !hasError.value,
					'peer-focus-visible:text-black-primary supportsHover:peer-hover:text-black-primary': !_ctx.disabled && !hasError.value,
					'text-gray-50': _ctx.disabled,
					'text-error': hasError.value,
				}])
      }, _toDisplayString(_ctx.label), 11, _hoisted_4),
      _createVNode(SvgIcon, {
        class: _normalizeClass(["pointer-events-none absolute right-4 top-1/2 size-5 -translate-y-1/2 transition-transform", {
					'rotate-180': customOptionsList_isOpen.value,
					'text-black-primary': isPopulated.value && !_ctx.disabled,
					'text-error': hasError.value,
					'text-gray-50': _ctx.disabled,
				}]),
        "icon-name": "arrow-down-thin"
      }, null, 8, ["class"]),
      _createElementVNode("ul", {
        ref: "customOptionsList",
        class: _normalizeClass(["absolute left-0 top-15 z-20 w-full overflow-hidden rounded text-black-primary shadow-4", { hidden: !customOptionsList_isOpen.value || !isCustomDropdown.value }]),
        style: {"clip-path":"inset(0 -20px -20px -20px)"}
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(generatedOptions), (option, key, i) => {
          return (_openBlock(), _createElementBlock("li", { key: key }, [
            _createElementVNode("button", {
              class: "flex h-12 w-full select-none items-center bg-white px-4 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-black-primary supportsHover:hover:bg-gray-40",
              style: {"-webkit-tap-highlight-color":"transparent"},
              "data-key": key,
              type: "button",
              tabindex: "-1",
              onClick: ($event: any) => (customDropDown_select(key))
            }, [
              (_unref(generatedIcons) && _unref(generatedIcons)[i])
                ? (_openBlock(), _createBlock(SvgIcon, {
                    key: 0,
                    class: "mr-2 size-5 shrink-0",
                    "icon-name": (_unref(generatedIcons)[i] as SpriteSymbol)
                  }, null, 8, ["icon-name"]))
                : (_unref(generatedImageUrls) && _unref(generatedImageUrls)[i])
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 1,
                      src: _unref(generatedImageUrls)[i],
                      alt: "",
                      class: "mr-2 size-5 shrink-0"
                    }, null, 8, _hoisted_6))
                  : _createCommentVNode("", true),
              _createElementVNode("span", {
                class: _normalizeClass(["block flex-1 text-left leading-none", { 'ml-7': _unref(generatedIcons) && !_unref(generatedIcons)[i] }])
              }, _toDisplayString(option), 3)
            ], 8, _hoisted_5)
          ]))
        }), 128))
      ], 2)
    ], 512),
    (_ctx.hint && (_ctx.hintPermanent || !_unref(isValidationVisible)))
      ? (_openBlock(), _createBlock(HintText, {
          key: 0,
          "no-indent": "",
          "hint-text": _ctx.hint
        }, null, 8, ["hint-text"]))
      : _createCommentVNode("", true),
    (_unref(isValidationVisible))
      ? (_openBlock(), _createBlock(ValidationText, {
          key: 1,
          "no-indent": "",
          "data-test-name": _ctx.dataTestName,
          "error-message": _unref(errorMessage)
        }, null, 8, ["data-test-name", "error-message"]))
      : _createCommentVNode("", true)
  ]))
}
}

})